import React, { useEffect } from 'react';
import { ToastType } from '@defa/defa-component-library';
import { useNotification } from '@utils/notification';

// Error deduplication system to prevent showing the same error multiple times
const errorDeduplicator = {
    recentErrors: new Map<string, number>(),
    dedupWindow: 3000, // 3 seconds

    shouldShow(message: string): boolean {
        const now = Date.now();
        const lastShown = this.recentErrors.get(message);

        if (!lastShown || now - lastShown > this.dedupWindow) {
            this.recentErrors.set(message, now);
            // Clean up old entries
            this.cleanup(now);
            return true;
        }

        return false;
    },

    cleanup(now: number) {
        for (const [message, timestamp] of this.recentErrors.entries()) {
            if (now - timestamp > this.dedupWindow) {
                this.recentErrors.delete(message);
            }
        }
    },
};

/**
 * Error listener component that integrates with the existing Toasts system
 * Listens for error events and shows them as notifications using the app's toast system
 * Includes deduplication to prevent showing the same error multiple times
 */
export const ErrorOverlay: React.FC = () => {
    const { add } = useNotification();

    useEffect(() => {
        const handleNetworkError = (event: CustomEvent<{ message: string }>) => {
            if (errorDeduplicator.shouldShow(event.detail.message)) {
                add({
                    title: '🌐 Network Error',
                    message: event.detail.message,
                    type: ToastType.ERROR,
                });
            }
        };

        const handleComponentError = (event: CustomEvent<{ message: string }>) => {
            if (errorDeduplicator.shouldShow(event.detail.message)) {
                add({
                    title: '⚠️ Component Error',
                    message: event.detail.message,
                    type: ToastType.ERROR,
                });
            }
        };

        // Listen for both network and component errors
        window.addEventListener('error-overlay-network' as any, handleNetworkError);
        window.addEventListener('error-overlay-component' as any, handleComponentError);

        return () => {
            window.removeEventListener('error-overlay-network' as any, handleNetworkError);
            window.removeEventListener('error-overlay-component' as any, handleComponentError);
        };
    }, [add]);

    // This component doesn't render anything - it just listens for errors
    return null;
};

// Utility functions to trigger toast notifications
export const showNetworkErrorOverlay = (message: string) => {
    window.dispatchEvent(
        new CustomEvent('error-overlay-network', {
            detail: { message },
        })
    );
};

export const showComponentErrorOverlay = (message: string) => {
    window.dispatchEvent(
        new CustomEvent('error-overlay-component', {
            detail: { message },
        })
    );
};
